import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {FormControl, FormField} from 'platform/components';
import {Box, Grid, Heading, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect} from 'react';
import {FieldPathValue, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil, sort} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {getSafely, noop, suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../hooks/useCatalogue';
import {usePriceReport} from '../../../hooks/usePriceReport';
import i18n from '../../../i18n/i18n';
import {useGetApiCountriesQuery, useGetTrimLevelsQuery} from '../../../store/catalogueApi';
import {DrillDownLevelName, DrillDownStatisticsLevel} from '../../../types/api/drillDown';
import {featureFlags} from '../../../utils/featureFlags';
import {getYearOptions} from '../../../utils/getYearOptions';
import {DRIVE_4X4} from '../constants/drive4x4';
import {EXCLUDED_FUEL_TYPES} from '../constants/excludedFuelTypes';
import {MAX_SEAT_COUNT} from '../constants/maxSeatCount';
import {WITHOUT_VAT} from '../constants/withoutVAT';
import {WITH_VAT} from '../constants/withVAT';
import {
  useFilterRelevantOptions,
  useGetRelevantPowerOptions,
} from '../hooks/useFilterRelevantOptions';
import {VehicleFormType} from '../types/VehicleFormType';
import {getFiltersFromFormData} from '../utils/getFiltersFromFormData';
import {DrillDownPicker} from './DrillDownPicker';
import {MakeModelFields} from './MakeModelFields';
import {PowerFields} from './PowerFields';
import {SeparatorWithLabel} from './SeparatorWithLabel';
import {VinParser} from './VinParser';

interface VehicleFieldsProps extends TestIdProps {
  control: FormControl<VehicleFormType>;
  formApi: UseFormReturn<VehicleFormType>;
}

const seatCountOptions = Array.from(Array(MAX_SEAT_COUNT)).map((_, index) => ({
  value: `${index + 1}`,
  label: `${index + 1}`,
}));

export function VehicleFields(props: VehicleFieldsProps) {
  const {
    fuelOptions: allFuelOptions,
    styleOptions,
    transmissionOptions,
    driveOptions,
    colorOptions,
    doorOptions,
    interiorMaterialOptions,
  } = useCatalogue();
  const {requestData} = usePriceReport();

  const allowedFuelOptions = allFuelOptions?.filter(
    (option) => !EXCLUDED_FUEL_TYPES.includes(option.value)
  );
  const firstRegistrationYearOptions = getYearOptions();

  const make = props.formApi.watch('make');
  const modelFamily = props.formApi.watch('modelFamily');
  const firstRegistrationOnYear = props.formApi.watch('firstRegistrationOnYear');
  const fuelType = props.formApi.watch('fuelType')?.[0];
  const power = props.formApi.watch('power');

  const {data: apiCountries, isLoading: isApiCountriesLoading} = useGetApiCountriesQuery({
    format: 'iso3',
  });
  const {currentData: trimLevels, isFetching: isTrimLevelsLoading} = useGetTrimLevelsQuery(
    isNil(make) || isNil(modelFamily)
      ? skipToken
      : {
          modelFamily,
          make,
          country: isNotNil(requestData?.market)
            ? getSafely(apiCountries, requestData.market)
            : undefined,
          yearFrom: firstRegistrationOnYear ?? undefined,
          yearTo: firstRegistrationOnYear ?? undefined,
        }
  );

  const hasTrimLevels =
    !isApiCountriesLoading && !isTrimLevelsLoading && isNotNilOrEmpty(trimLevels?.trim_levels);
  const trimLevelsOptions = sort(
    (a, b) => {
      if (isNotNil(a.label) && isNotNil(b.label)) {
        return a.label.localeCompare(b.label);
      }
      return 0;
    },
    trimLevels?.trim_levels?.map((trimLevel) => ({
      label: trimLevel.label,
      value: trimLevel.const_key,
    })) ?? []
  );

  useEffect(() => {
    if (
      isNotNil(trimLevels) &&
      !trimLevels.trim_levels
        ?.map((trimLevel) => trimLevel.const_key)
        .includes(props.formApi.getValues('trimLevel'))
    ) {
      props.formApi.setValue('trimLevel', '');
    }
  }, [trimLevels, props.formApi]);

  const values = props.formApi.getValues();
  const transmissionOptionsFiltered = useFilterRelevantOptions(
    transmissionOptions ?? [],
    'transmission',
    getFiltersFromFormData(values, 'transmission')
  );
  const fuelTypeOptionsFiltered = useFilterRelevantOptions(
    allowedFuelOptions ?? [],
    'fuel_type',
    getFiltersFromFormData(values, 'fuel_type')
  );
  const bodyStyleOptionsFiltered = useFilterRelevantOptions(
    styleOptions ?? [],
    'car_style',
    getFiltersFromFormData(values, 'car_style')
  );
  const driveOptionsFiltered = useFilterRelevantOptions(
    driveOptions ?? [],
    'drive',
    getFiltersFromFormData(values, 'drive')
  );
  const is4x4Options = driveOptionsFiltered.map((option) => ({
    label:
      option.value === DRIVE_4X4
        ? i18n.t('page.create.labels.yes')
        : i18n.t('page.create.labels.no'),
    value: option.value,
    suffix: option.suffix,
  }));
  const powerOptions = useGetRelevantPowerOptions(getFiltersFromFormData(values, 'power'));
  const onFilterChange = (
    name: keyof VehicleFormType,
    value: FieldPathValue<VehicleFormType, keyof VehicleFormType>
  ) => {
    props.formApi.setValue(name, value, {shouldTouch: true});
    props.control.onChange();
  };
  const onDrillDownRowSelect = (levels: DrillDownStatisticsLevel[]) => {
    levels.forEach((level) => {
      match<DrillDownLevelName, void>(level.name)
        .with('year', () => props.formApi.setValue('firstRegistrationOnYear', Number(level.value)))
        .with('fuel_type', () => props.formApi.setValue('fuelType', [level.value]))
        .with('transmission', () => props.formApi.setValue('transmission', [level.value]))
        .with('drive', () => props.formApi.setValue('drive', [level.value]))
        .with('power', () => props.formApi.setValue('power', Number(level.value)))
        .with('car_style', () => props.formApi.setValue('bodyStyle', [level.value]))
        .otherwise(noop);
    });
    props.control.onChange();
  };

  return (
    <VStack spacing={4}>
      <Heading size={2}>{i18n.t('page.create.labels.vehicleSpecification')}</Heading>
      <VinParser
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('vinParser', props)}
      />
      <SeparatorWithLabel label={i18n.t('page.create.labels.basicInformation')} />
      <MakeModelFields
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('makeModelFields', props)}
      />
      <Grid columns={[1, 1, 2]} spacing={6}>
        <FormField
          control={props.control}
          type="choice"
          name="firstRegistrationOnYear"
          label={i18n.t('page.create.labels.firstRegistrationOnYear')}
          isRequired
          options={firstRegistrationYearOptions}
          data-testid={suffixTestId('form-firstRegistrationOnYear', props)}
        />
        <FormField
          control={props.control}
          type="choice"
          name="trimLevel"
          label={i18n.t('page.create.labels.trimLevel')}
          options={trimLevelsOptions}
          isDisabled={!hasTrimLevels}
          data-testid={suffixTestId('form-trimLevel', props)}
        />
      </Grid>
      <SeparatorWithLabel label={i18n.t('page.create.labels.detailedInformation')} />
      <FormField
        control={props.control}
        type="chips"
        name="fuelType"
        label={i18n.t('page.create.labels.fuelType')}
        isRequired
        isDeselectable
        options={fuelTypeOptionsFiltered}
        data-testid={suffixTestId('form-fuelType', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="transmission"
        label={i18n.t('page.create.labels.transmission')}
        isRequired
        isDeselectable
        options={transmissionOptionsFiltered}
        data-testid={suffixTestId('form-transmission', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="drive"
        label={i18n.t('page.create.labels.drive4x4')}
        isRequired
        isDeselectable
        options={is4x4Options}
        data-testid={suffixTestId('form-drive', props)}
      />
      <PowerFields
        power={power}
        fuelType={fuelType}
        powerOptions={powerOptions}
        control={props.control}
        isManualEnterDisabled={isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)}
        onFilterChange={onFilterChange}
        data-testid={suffixTestId('form-power', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="bodyStyle"
        label={i18n.t('page.create.labels.bodyStyle')}
        isRequired
        isDeselectable
        options={bodyStyleOptionsFiltered}
        data-testid={suffixTestId('form-bodyStyle', props)}
      />
      <DrillDownPicker
        filters={getFiltersFromFormData(values)}
        onRowSelect={onDrillDownRowSelect}
        data-testid={suffixTestId('createDrillDown', props)}
      />
      <SeparatorWithLabel label={i18n.t('page.create.labels.additionalInformation')} />
      <FormField
        control={props.control}
        type="chips"
        name="doorCount"
        label={i18n.t('page.create.labels.doorCount')}
        options={doorOptions ?? []}
        isDeselectable
        data-testid={suffixTestId('form-doorCount', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="seatCount"
        label={i18n.t('page.create.labels.seatCount')}
        options={seatCountOptions}
        isDeselectable
        data-testid={suffixTestId('form-seatCount', props)}
      />
      <Grid columns={[1, 2]} spacing={6}>
        <Box flex={1}>
          <FormField
            control={props.control}
            type="number"
            name="mileage"
            label={`${i18n.t('page.create.labels.mileage')} (${i18n.t('general.metric.km')})`}
            isRequired
            data-testid={suffixTestId('form-mileage', props)}
          />
        </Box>
        <Show onDesktop onNotebook>
          <Space fillAvailable />
        </Show>
      </Grid>
      <FormField
        control={props.control}
        type="chips"
        name="isVatDeductible"
        label={i18n.t('page.create.labels.vatDeductible')}
        options={[
          {label: i18n.t('page.create.labels.yes'), value: WITH_VAT},
          {label: i18n.t('page.create.labels.no'), value: WITHOUT_VAT},
        ]}
        isDeselectable
        data-testid={suffixTestId('form-isVatDeductible', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="exteriorColor"
        label={i18n.t('page.create.labels.exteriorColor')}
        options={colorOptions ?? []}
        isDeselectable
        data-testid={suffixTestId('form-exteriorColor', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="interiorMaterial"
        label={i18n.t('page.create.labels.interiorMaterial')}
        options={interiorMaterialOptions ?? []}
        isDeselectable
        data-testid={suffixTestId('form-interiorMaterial', props)}
      />
    </VStack>
  );
}
